"use client";

import {
  addDays,
  addMonths,
  format as gFormat,
  getDay,
  getDaysInMonth,
  startOfMonth,
} from "date-fns";
import {
  addMonths as jAddMonths,
  format as jFormat,
  getDaysInMonth as jGetDaysInMonth,
  startOfMonth as jStartOfMonth,
} from "date-fns-jalali";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import useSWR from "swr";
import apis from "@/configs/apis";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import {
  getHotelCityNameByCode,
  HOTEL_CITY_OPTIONS,
  type HotelCityOption,
} from "@/shared/constants/hotelCities";
import fetcher, { type ExtendedResponse } from "@/shared/libs/fetcher";
import { parseHotelResultsV2Date, toHotelResultsV2FaNumber } from "../utils";

interface SearchSummaryProps {
  onSearch: () => void;
  onMobileSearchOpenChange?: (open: boolean) => void;
}

type RoomGuests = { adults: number; children: number };

const SEARCH_CONTROL_RADIUS = "rounded-[12px]";
const SEARCH_CONTROL_RADIUS_STYLE = {
  borderRadius: 12,
};
const SEARCH_SOFT_PRIMARY_BG = "bg-primary/10";
const SEARCH_SOFT_PRIMARY_HOVER_BG = "hover:bg-primary/15";

const getInitialSearchDate = (checkIn?: string | null) => {
  if (checkIn) {
    try {
      return parseHotelResultsV2Date(checkIn, getTodayStart());
    } catch {
      return getTodayStart();
    }
  }

  return getTodayStart();
};

const getTodayStart = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

export default function SearchSummary({
  onSearch,
  onMobileSearchOpenChange,
}: SearchSummaryProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchName = searchParams.get("name");
  const searchCheckIn = searchParams.get("checkIn");
  const searchCheckOut = searchParams.get("checkOut");
  const searchRooms = searchParams.get("rooms");
  const initialCityCode = getInitialCityCode(pathname);
  const initialCityName = searchName ?? getInitialCityName(initialCityCode);
  const [destination, setDestination] = useState<HotelCityOption>({
    name: initialCityName,
    sub: initialCityCode,
    code: initialCityCode,
  });
  const [selectedDate, setSelectedDate] = useState<Date>(() =>
    getInitialSearchDate(searchCheckIn),
  );
  const [nights, setNights] = useState(() =>
    getInitialNights(searchCheckIn, searchCheckOut),
  );
  const [rooms, setRooms] = useState<RoomGuests[]>(() =>
    parseInitialRooms(searchRooms),
  );
  const [openPanel, setOpenPanel] = useState<
    "city" | "date" | "nights" | "passengers" | null
  >(null);
  const [cityQuery, setCityQuery] = useState("");
  const [autoFlow, setAutoFlow] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { data: citiesResponse, isLoading: citiesLoading } = useSWR<
    ExtendedResponse<CitiesRes>
  >(`${apis.hotel.cities.client}?q=`, fetcher, {
    revalidateOnMount: true,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  useEffect(() => {
    const nextCityCode = getInitialCityCode(pathname);
    const nextCityName = searchName ?? getInitialCityName(nextCityCode);

    setDestination({
      name: nextCityName,
      sub: nextCityCode,
      code: nextCityCode,
    });
    setSelectedDate(getInitialSearchDate(searchCheckIn));
    setNights(getInitialNights(searchCheckIn, searchCheckOut));
    setRooms(parseInitialRooms(searchRooms));
  }, [pathname, searchCheckIn, searchCheckOut, searchName, searchRooms]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpenPanel(null);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    onMobileSearchOpenChange?.(mobileSearchOpen);
  }, [mobileSearchOpen, onMobileSearchOpenChange]);

  const filteredCities = useMemo(() => {
    const cities = mapApiCities(citiesResponse?.data?.results);
    const options = cities.length ? cities : HOTEL_CITY_OPTIONS;
    const query = cityQuery.trim().toLowerCase();

    if (!query) return options;

    return options.filter((city) =>
      [city.name, city.sub, city.code].some((field) =>
        field.toLowerCase().includes(query),
      ),
    );
  }, [citiesResponse?.data?.results, cityQuery]);

  const totalGuests = rooms.reduce(
    (sum, room) => sum + room.adults + room.children,
    0,
  );

  const dateLabel = jFormat(selectedDate, "d MMMM");
  const checkout = addDays(selectedDate, nights);
  const mobileSummary = `${destination.name}، ${dateLabel}، ${toHotelResultsV2FaNumber(nights)} شب، ${toHotelResultsV2FaNumber(totalGuests)} نفر`;
  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    setOpenPanel(null);
    setAutoFlow(false);
  };
  const runSearch = () => {
    setMobileSearchOpen(false);
    setOpenPanel(null);
    setAutoFlow(false);
    onSearch();
    router.push(
      createHotelSearchUrl({
        city: destination,
        selectedDate,
        nights,
        rooms,
        currentPath: pathname,
      }),
    );
  };

  return (
    <section
      ref={wrapperRef}
      className="sticky top-0 z-30 border-b border-border bg-white"
      dir="rtl"
    >
      <div className="customContainer mx-auto py-2 max-md:!px-4 md:py-2.5">
        <div className="md:hidden">
          <button
            onClick={() => {
              setMobileSearchOpen(true);
              setOpenPanel(null);
              setAutoFlow(false);
            }}
            className={`flex w-full items-center gap-3 ${SEARCH_CONTROL_RADIUS} ${SEARCH_SOFT_PRIMARY_BG} px-4 py-3 text-right transition ${SEARCH_SOFT_PRIMARY_HOVER_BG}`}
            style={SEARCH_CONTROL_RADIUS_STYLE}
            type="button"
          >
            <PhosphorIcon
              name="CalendarBlank"
              size={20}
              weight="bold"
              className="shrink-0 text-primary"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[16px] font-semibold text-foreground">
                {mobileSummary}
              </span>
            </span>
            <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-[14px] font-bold text-white">
              تغییر تاریخ
            </span>
          </button>
        </div>

        <div className="hidden items-stretch gap-1.5 md:flex">
          <div className="relative w-[22%] min-w-36">
            <SearchField
              icon="MapPin"
              label="مقصد"
              value={destination.name}
              valueClassName="text-[14px] text-foreground"
              onClick={() => {
                setAutoFlow(true);
                setOpenPanel(openPanel === "city" ? null : "city");
              }}
            />
            {openPanel === "city" && (
              <CityPanel
                query={cityQuery}
                cities={filteredCities}
                loading={citiesLoading}
                selected={destination}
                onQueryChange={setCityQuery}
                onSelect={(city) => {
                  setDestination(city);
                  setCityQuery("");
                  setOpenPanel(autoFlow ? "date" : null);
                }}
              />
            )}
          </div>

          <div className="relative w-[20%] min-w-32">
            <SearchField
              icon="CalendarBlank"
              label="تاریخ ورود"
              value={dateLabel}
              valueClassName="text-[14px] text-foreground"
              showCaret={false}
              onClick={() => {
                setAutoFlow(false);
                setOpenPanel(openPanel === "date" ? null : "date");
              }}
            />
            {openPanel === "date" && (
              <DatePanel
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                  setOpenPanel(autoFlow ? "nights" : null);
                }}
              />
            )}
          </div>

          <div className="relative w-[28%] min-w-40">
            <SearchField
              icon="Moon"
              label="تعداد شب"
              value={`${toHotelResultsV2FaNumber(nights)} شب`}
              suffix={`تا ${jFormat(checkout, "d MMMM")}`}
              valueClassName="text-[16px] font-semibold text-foreground"
              onClick={() => {
                setAutoFlow(false);
                setOpenPanel(openPanel === "nights" ? null : "nights");
              }}
            />
            {openPanel === "nights" && (
              <NightsPanel
                selectedDate={selectedDate}
                nights={nights}
                onSelect={(value) => {
                  setNights(value);
                  setOpenPanel(autoFlow ? "passengers" : null);
                }}
              />
            )}
          </div>

          <div className="relative min-w-0 flex-1">
            <SearchField
              icon="Users"
              label="مسافران"
              value={`${toHotelResultsV2FaNumber(totalGuests)} مسافر، ${toHotelResultsV2FaNumber(rooms.length)} اتاق`}
              valueClassName="text-[14px] font-medium text-foreground"
              onClick={() => {
                setAutoFlow(false);
                setOpenPanel(openPanel === "passengers" ? null : "passengers");
              }}
            />
            {openPanel === "passengers" && (
              <PassengerPanel
                rooms={rooms}
                onChange={setRooms}
                onClose={() => {
                  setOpenPanel(null);
                  if (autoFlow) {
                    runSearch();
                  }
                }}
              />
            )}
          </div>

          <button
            onClick={runSearch}
            disabled={!destination.code}
            className={`flex shrink-0 items-center gap-2 ${SEARCH_CONTROL_RADIUS} bg-primary px-5 py-2 text-[16px] font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60`}
            style={SEARCH_CONTROL_RADIUS_STYLE}
            type="button"
          >
            <PhosphorIcon name="MagnifyingGlass" size={15} />
            جستجو هتل
          </button>
        </div>
      </div>

      {mobileSearchOpen && (
        <div
          className="fixed inset-0 z-[300] overflow-y-auto bg-white md:hidden"
          dir="rtl"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-white px-4 py-4">
            <div>
              <p className="text-[14px] text-muted-foreground">
                ویرایش جستجوی هتل
              </p>
              <p className="text-[18px] font-bold text-foreground">
                {destination.name}
              </p>
            </div>
            <button
              onClick={closeMobileSearch}
              className="grid size-10 place-items-center rounded-full bg-primary/10 text-primary"
              type="button"
              aria-label="بستن فرم جستجو"
            >
              <PhosphorIcon name="X" size={18} />
            </button>
          </div>

          <div className="space-y-3 px-4 py-5">
            <SearchField
              icon="MapPin"
              label="مقصد"
              value={destination.name}
              className="py-3.5"
              valueClassName="text-[16px] text-foreground"
              onClick={() => {
                setAutoFlow(true);
                setOpenPanel(openPanel === "city" ? null : "city");
              }}
            />
            <SearchField
              icon="CalendarBlank"
              label="تاریخ ورود"
              value={dateLabel}
              className="py-3.5"
              valueClassName="text-[16px] text-foreground"
              showCaret={false}
              onClick={() => {
                setAutoFlow(false);
                setOpenPanel(openPanel === "date" ? null : "date");
              }}
            />
            <SearchField
              icon="Moon"
              label="تعداد شب"
              value={`${toHotelResultsV2FaNumber(nights)} شب`}
              className="py-3.5"
              valueClassName="text-[16px] text-foreground"
              showCaret={false}
              onClick={() => {
                setAutoFlow(false);
                setOpenPanel(openPanel === "nights" ? null : "nights");
              }}
            />
            <SearchField
              icon="Users"
              label="مسافران"
              value={`${toHotelResultsV2FaNumber(totalGuests)} مسافر، ${toHotelResultsV2FaNumber(rooms.length)} اتاق`}
              className="py-3.5"
              valueClassName="text-[16px] text-foreground"
              showCaret={false}
              onClick={() => {
                setAutoFlow(false);
                setOpenPanel(openPanel === "passengers" ? null : "passengers");
              }}
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-white p-4">
            <button
              onClick={runSearch}
              className={`flex w-full items-center justify-center gap-2 ${SEARCH_CONTROL_RADIUS} bg-primary py-3.5 text-[16px] font-bold text-white`}
              style={SEARCH_CONTROL_RADIUS_STYLE}
              type="button"
            >
              <PhosphorIcon name="MagnifyingGlass" size={18} />
              جستجو هتل
            </button>
          </div>
        </div>
      )}

      {openPanel && (
        <MobilePanel openPanel={openPanel} onClose={() => setOpenPanel(null)}>
          {openPanel === "city" && (
            <CityPanel
              mobile
              query={cityQuery}
              cities={filteredCities}
              loading={citiesLoading}
              selected={destination}
              onQueryChange={setCityQuery}
              onSelect={(city) => {
                setDestination(city);
                setCityQuery("");
                setOpenPanel(autoFlow ? "date" : null);
              }}
            />
          )}
          {openPanel === "date" && (
            <DatePanel
              mobile
              selected={selectedDate}
              onSelect={(date) => {
                setSelectedDate(date);
                setOpenPanel(autoFlow ? "nights" : null);
              }}
            />
          )}
          {openPanel === "nights" && (
            <NightsPanel
              mobile
              selectedDate={selectedDate}
              nights={nights}
              onSelect={(value) => {
                setNights(value);
                setOpenPanel(autoFlow ? "passengers" : null);
              }}
            />
          )}
          {openPanel === "passengers" && (
            <PassengerPanel
              mobile
              rooms={rooms}
              onChange={setRooms}
              onClose={() => {
                setOpenPanel(null);
                if (autoFlow) {
                  runSearch();
                }
              }}
            />
          )}
        </MobilePanel>
      )}
    </section>
  );
}

function getInitialCityCode(pathname: string) {
  if (pathname.startsWith("/playground/hotel-results-v2")) return "KIH";

  const match = pathname.match(/\/hotel\/([^/]+)/);
  return match?.[1] || "KIH";
}

function getInitialCityName(code: string) {
  return getHotelCityNameByCode(code) ?? "کیش";
}

function getInitialNights(checkIn?: string | null, checkOut?: string | null) {
  if (!checkIn || !checkOut) return 1;

  const start = parseHotelResultsV2Date(checkIn, getTodayStart());
  const end = parseHotelResultsV2Date(checkOut, addDays(start, 1));
  const diff = Math.round((end.getTime() - start.getTime()) / 86_400_000);

  return Number.isFinite(diff) && diff > 0 ? diff : 1;
}

function parseInitialRooms(value?: string | null): RoomGuests[] {
  if (!value) return [{ adults: 2, children: 0 }];

  const rooms = value
    .split("_")
    .map((roomValue) => {
      const parts = roomValue.split("-").filter(Boolean);
      const adults = Number(parts[0]);

      return {
        adults: Number.isFinite(adults) && adults > 0 ? adults : 1,
        children: Math.max(0, parts.length - 1),
      };
    })
    .filter((room) => room.adults > 0);

  return rooms.length ? rooms : [{ adults: 2, children: 0 }];
}

function mapApiCities(cities?: Citys[]): HotelCityOption[] {
  if (!cities?.length) return [];

  return cities.map((city) => ({
    name: city.fa_name || city.name,
    sub: city.name || city.iata,
    code: city.iata,
  }));
}

function createHotelSearchUrl({
  city,
  selectedDate,
  nights,
  rooms,
  currentPath,
}: {
  city: HotelCityOption;
  selectedDate: Date;
  nights: number;
  rooms: RoomGuests[];
  currentPath: string;
}) {
  const checkIn = jFormat(selectedDate, "yyyy-MM-dd");
  const checkOut = jFormat(addDays(selectedDate, nights), "yyyy-MM-dd");
  const params = new URLSearchParams({
    checkIn,
    name: city.name,
    checkOut,
    rooms: encodeRoomsForHotelSearch(rooms),
  });

  if (currentPath.startsWith("/playground/hotel-results-v2")) {
    params.set("city", city.code);
    return `${currentPath}?${params.toString()}`;
  }

  return `/hotel/${city.code}/?${params.toString()}`;
}

function encodeRoomsForHotelSearch(rooms: RoomGuests[]) {
  return rooms
    .map((room) => {
      const childAges = Array.from({ length: room.children }, () => "5");
      return [room.adults, ...childAges].join("-");
    })
    .join("_");
}

function SearchField({
  icon,
  label,
  value,
  suffix,
  className,
  valueClassName,
  showCaret = true,
  onClick,
}: {
  icon: "MapPin" | "CalendarBlank" | "Moon" | "Users";
  label: string;
  value: string;
  suffix?: string;
  className?: string;
  valueClassName?: string;
  showCaret?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex h-full w-full cursor-pointer items-center gap-2 ${SEARCH_CONTROL_RADIUS} ${SEARCH_SOFT_PRIMARY_BG} px-4 py-3 text-right transition ${SEARCH_SOFT_PRIMARY_HOVER_BG} ${className ?? ""}`}
      style={SEARCH_CONTROL_RADIUS_STYLE}
      type="button"
    >
      <PhosphorIcon name={icon} size={14} className="shrink-0 text-primary" />
      <span className="min-w-0 flex-1">
        <span className="mb-1 block text-[14px] leading-none text-muted-foreground">
          {label}
        </span>
        <span className="flex min-w-0 items-center justify-between gap-1">
          <span
            className={`block min-w-0 truncate leading-none ${
              valueClassName ?? "text-[14px] font-medium text-foreground"
            }`}
          >
            {value}
          </span>
          {suffix && (
            <span className="truncate text-[14px] leading-none text-muted-foreground">
              {suffix}
            </span>
          )}
        </span>
      </span>
      {showCaret && (
        <PhosphorIcon
          name="CaretDown"
          size={12}
          className="shrink-0 text-muted-foreground"
        />
      )}
    </button>
  );
}

function CityPanel({
  cities,
  loading,
  query,
  selected,
  mobile,
  onQueryChange,
  onSelect,
}: {
  cities: HotelCityOption[];
  loading?: boolean;
  query: string;
  selected: HotelCityOption;
  mobile?: boolean;
  onQueryChange: (value: string) => void;
  onSelect: (city: HotelCityOption) => void;
}) {
  return (
    <div
      className={
        mobile
          ? "flex h-full min-h-0 w-full flex-col bg-white"
          : "absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-2xl border border-border bg-white shadow-2xl"
      }
    >
      <div
        className={
          mobile
            ? "sticky top-0 z-10 border-b border-border bg-white p-3"
            : "border-b border-border p-2.5"
        }
      >
        <div
          className={`flex items-center gap-2 ${SEARCH_CONTROL_RADIUS} ${SEARCH_SOFT_PRIMARY_BG} px-4 py-3`}
          style={SEARCH_CONTROL_RADIUS_STYLE}
        >
          <PhosphorIcon
            name="MagnifyingGlass"
            size={13}
            className="text-muted-foreground"
          />
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-muted-foreground"
            placeholder="جستجوی شهر..."
            autoFocus
          />
          {query && (
            <button onClick={() => onQueryChange("")} type="button">
              <PhosphorIcon name="X" size={12} className="text-slate-500" />
            </button>
          )}
        </div>
      </div>
      <div
        className={
          mobile
            ? "min-h-0 flex-1 overflow-y-auto py-1.5 pb-6"
            : "max-h-60 overflow-y-auto py-1.5"
        }
      >
        {loading && (
          <div className="space-y-2 px-4 py-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-10 animate-pulse rounded-xl bg-primary/10"
              />
            ))}
          </div>
        )}
        {!loading &&
          cities.map((city) => (
            <button
              key={city.code}
              onClick={() => onSelect(city)}
              className={`flex w-full items-center justify-between px-4 py-2.5 text-right transition ${SEARCH_SOFT_PRIMARY_HOVER_BG} ${
                selected.code === city.code ? SEARCH_SOFT_PRIMARY_BG : ""
              }`}
              type="button"
            >
              <span>
                <span className="block text-[16px] font-semibold text-foreground">
                  {city.name}
                </span>
                <span className="block text-[14px] text-muted-foreground">
                  {city.sub}
                </span>
              </span>
              {selected.code === city.code && (
                <span className="size-2 rounded-full bg-primary" />
              )}
            </button>
          ))}
        {!loading && cities.length === 0 && (
          <p className="px-4 py-4 text-center text-[14px] text-muted-foreground">
            شهری با این نام پیدا نشد.
          </p>
        )}
      </div>
    </div>
  );
}

function DatePanel({
  selected,
  mobile,
  onSelect,
}: {
  selected: Date;
  mobile?: boolean;
  onSelect: (date: Date) => void;
}) {
  const [calendar, setCalendar] = useState<"jalali" | "gregorian">("jalali");
  const [viewDate, setViewDate] = useState(selected);
  const isJalali = calendar === "jalali";
  const selectedKey = isJalali
    ? jFormat(selected, "yyyy-MM-dd")
    : gFormat(selected, "yyyy-MM-dd");
  const visibleMonths = [viewDate, getNextCalendarMonth(viewDate, isJalali)];
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const goPrev = () =>
    setViewDate((current) =>
      isJalali ? jAddMonths(current, -1) : addMonths(current, -1),
    );
  const goNext = () =>
    setViewDate((current) =>
      isJalali ? jAddMonths(current, 1) : addMonths(current, 1),
    );

  return (
    <div
      className={
        mobile
          ? "w-full p-3"
          : "absolute right-0 top-full z-50 mt-1 w-[680px] rounded-2xl border border-border bg-white p-4 shadow-2xl"
      }
    >
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={goPrev}
          type="button"
          className="grid size-8 place-items-center rounded-full transition hover:bg-muted"
        >
          <PhosphorIcon
            name="CaretRight"
            size={16}
            className="text-muted-foreground"
          />
        </button>
        <p className="hidden text-center text-[16px] font-bold text-foreground md:block">
          انتخاب تاریخ ورود
        </p>
        <button
          onClick={goNext}
          type="button"
          className="grid size-8 place-items-center rounded-full transition hover:bg-muted"
        >
          <PhosphorIcon
            name="CaretLeft"
            size={16}
            className="text-muted-foreground"
          />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-3">
        {visibleMonths.map((monthDate) => {
          const monthStart = isJalali
            ? jStartOfMonth(monthDate)
            : startOfMonth(monthDate);
          const daysCount = isJalali
            ? jGetDaysInMonth(monthDate)
            : getDaysInMonth(monthDate);
          const offset = (getDay(monthStart) + 1) % 7;
          const days = Array.from({ length: daysCount }, (_, index) =>
            addDays(monthStart, index),
          );
          const monthLabel = isJalali
            ? jFormat(monthDate, "MMMM yyyy")
            : gFormat(monthDate, "MMM yyyy");

          return (
            <div key={monthLabel} className="min-w-0">
              <p className="mb-2 truncate text-center text-[14px] font-bold text-foreground md:text-[15px]">
                {monthLabel}
              </p>
              <div className="mb-1 grid grid-cols-7 text-center text-[11px] text-muted-foreground md:text-[13px]">
                {["ش", "ی", "د", "س", "چ", "پ", "ج"].map((day) => (
                  <span key={`${monthLabel}-${day}`} className="py-1">
                    {day}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {Array.from({ length: offset }).map((_, index) => (
                  <span key={`${monthLabel}-empty-${index}`} />
                ))}
                {days.map((date) => {
                  const dateKey = isJalali
                    ? jFormat(date, "yyyy-MM-dd")
                    : gFormat(date, "yyyy-MM-dd");
                  const active = selectedKey === dateKey;
                  const isPast = date < todayStart;

                  return (
                    <button
                      key={dateKey}
                      onClick={() => onSelect(date)}
                      disabled={isPast}
                      className={`grid place-items-center text-[12px] transition md:text-[14px] ${
                        mobile ? "h-7 rounded-lg" : "h-8 rounded-xl"
                      } ${
                        active
                          ? "bg-primary font-bold text-white"
                          : isPast
                            ? "cursor-not-allowed text-muted-foreground/40"
                            : `text-foreground ${SEARCH_SOFT_PRIMARY_HOVER_BG}`
                      }`}
                      type="button"
                    >
                      {isJalali ? jFormat(date, "d") : date.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex justify-start">
        <button
          onClick={() => {
            setCalendar((value) =>
              value === "jalali" ? "gregorian" : "jalali",
            );
            setViewDate(selected);
          }}
          className={`${SEARCH_CONTROL_RADIUS} border px-3 py-1 text-[14px] font-medium transition ${SEARCH_SOFT_PRIMARY_HOVER_BG}`}
          style={{
            ...SEARCH_CONTROL_RADIUS_STYLE,
            borderColor: isJalali ? "#e5e7eb" : "#e84040",
            color: isJalali ? "#9ca3af" : "#e84040",
          }}
          type="button"
        >
          {isJalali ? "تقویم میلادی" : "تقویم شمسی"}
        </button>
      </div>
    </div>
  );
}

function getNextCalendarMonth(date: Date, isJalali: boolean) {
  return isJalali ? jAddMonths(date, 1) : addMonths(date, 1);
}

function NightsPanel({
  selectedDate,
  nights,
  mobile,
  onSelect,
}: {
  selectedDate: Date;
  nights: number;
  mobile?: boolean;
  onSelect: (nights: number) => void;
}) {
  return (
    <div
      className={
        mobile
          ? "max-h-[60vh] overflow-y-auto py-2"
          : "absolute right-0 top-full z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-2xl border border-border bg-white py-1 shadow-xl"
      }
    >
      {Array.from({ length: 20 }, (_, index) => index + 1).map((value) => {
        const checkout = addDays(selectedDate, value);

        return (
          <button
            key={value}
            onClick={() => onSelect(value)}
            className={`flex w-full items-center justify-between border-b border-border/50 px-4 py-2.5 text-[16px] transition ${SEARCH_SOFT_PRIMARY_HOVER_BG} ${
              nights === value
                ? `${SEARCH_SOFT_PRIMARY_BG} font-bold text-primary`
                : "text-foreground"
            }`}
            type="button"
          >
            <span>{toHotelResultsV2FaNumber(value)} شب</span>
            <span className="text-[14px] text-muted-foreground">
              تا {jFormat(checkout, "d MMMM")}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PassengerPanel({
  rooms,
  mobile,
  onChange,
  onClose,
}: {
  rooms: RoomGuests[];
  mobile?: boolean;
  onChange: (rooms: RoomGuests[]) => void;
  onClose: () => void;
}) {
  const update = (index: number, field: keyof RoomGuests, delta: number) => {
    onChange(
      rooms.map((room, roomIndex) =>
        roomIndex === index
          ? {
              ...room,
              [field]: Math.max(
                field === "adults" ? 1 : 0,
                Math.min(field === "adults" ? 6 : 4, room[field] + delta),
              ),
            }
          : room,
      ),
    );
  };

  const addRoom = () => {
    if (rooms.length < 5) onChange([...rooms, { adults: 1, children: 0 }]);
  };

  const removeRoom = (index: number) => {
    if (rooms.length > 1) {
      onChange(rooms.filter((_, roomIndex) => roomIndex !== index));
    }
  };

  const totalGuests = rooms.reduce(
    (sum, room) => sum + room.adults + room.children,
    0,
  );

  return (
    <div
      className={
        mobile
          ? "w-full"
          : "absolute right-0 top-full z-50 mt-1 w-full rounded-2xl border border-border bg-white shadow-2xl"
      }
    >
      <div className="max-h-[420px] space-y-4 overflow-y-auto p-4">
        {rooms.map((room, index) => (
          <div key={index}>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-[16px] font-bold text-foreground">
                اتاق {toHotelResultsV2FaNumber(index + 1)}
              </p>
              {rooms.length > 1 && (
                <button
                  onClick={() => removeRoom(index)}
                  className="text-[14px] text-red-400 transition hover:text-red-600"
                  type="button"
                >
                  حذف
                </button>
              )}
            </div>
            <PassengerRow
              title="بزرگسال"
              subtitle="۱۲ سال به بالا"
              value={room.adults}
              min={1}
              onMinus={() => update(index, "adults", -1)}
              onPlus={() => update(index, "adults", 1)}
            />
            <PassengerRow
              title="کودک"
              subtitle="تا ۱۲ سال"
              value={room.children}
              min={0}
              onMinus={() => update(index, "children", -1)}
              onPlus={() => update(index, "children", 1)}
            />
            {index < rooms.length - 1 && (
              <div className="mt-2 border-t border-border" />
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-border px-4 pb-3 pt-3">
        {rooms.length < 5 && (
          <button
            onClick={addRoom}
            className="mb-3 flex items-center gap-1.5 text-[16px] font-semibold text-primary"
            type="button"
          >
            <PhosphorIcon name="Plus" size={15} />
            افزودن اتاق
          </button>
        )}
        <button
          onClick={onClose}
          className={`w-full ${SEARCH_CONTROL_RADIUS} bg-primary py-2.5 text-[16px] font-bold text-white`}
          style={SEARCH_CONTROL_RADIUS_STYLE}
          type="button"
        >
          تایید — {toHotelResultsV2FaNumber(totalGuests)} مسافر و{" "}
          {toHotelResultsV2FaNumber(rooms.length)} اتاق
        </button>
      </div>
    </div>
  );
}

function PassengerRow({
  title,
  subtitle,
  value,
  min,
  onMinus,
  onPlus,
}: {
  title: string;
  subtitle: string;
  value: number;
  min: number;
  onMinus: () => void;
  onPlus: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-[16px] text-slate-950">{title}</p>
        <p className="text-[14px] text-slate-500">{subtitle}</p>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onMinus}
          disabled={value <= min}
          className="grid size-8 place-items-center rounded-full border-2 border-primary text-primary disabled:opacity-30"
          type="button"
        >
          <PhosphorIcon name="Minus" size={14} />
        </button>
        <span className="w-5 text-center text-[16px] font-bold">
          {toHotelResultsV2FaNumber(value)}
        </span>
        <button
          onClick={onPlus}
          className="grid size-8 place-items-center rounded-full border-2 border-primary bg-primary text-white"
          type="button"
        >
          <PhosphorIcon name="Plus" size={14} />
        </button>
      </div>
    </div>
  );
}

function MobilePanel({
  openPanel,
  children,
  onClose,
}: {
  openPanel: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[320] flex flex-col justify-end md:hidden"
      dir="rtl"
    >
      <button
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        type="button"
        aria-label="بستن"
      />
      <div className="relative flex max-h-[86vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white">
        <div className="border-b border-border px-4 pb-2 pt-4">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300" />
          <p className="text-center text-[16px] font-bold text-slate-950">
            {getMobilePanelTitle(openPanel)}
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function getMobilePanelTitle(openPanel: string) {
  if (openPanel === "city") return "انتخاب مقصد";
  if (openPanel === "date") return "انتخاب تاریخ ورود";
  if (openPanel === "nights") return "تعداد شب";
  return "مسافران و اتاق‌ها";
}
